import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Link,
  useParams,
} from 'react-router-dom';

import {
  useTranslation,
} from 'react-i18next';

import {
  useAuth,
} from '../../auth/AuthContext';

import {
  getUpdateNotice,
} from '../services/updateNoticeService';

import {
  formatNoticeDate,
  getNoticeTitle,
} from '../config/updateNoticeConfig';

import {
  TOP_LEVEL_NUMBERED_LINE_REGEX,
  extractTopLevelNumberedLines,
  getChildContentLevel,
  getHierarchicalNumberingLevel,
  getImageMarkerCaption,
  getImageMarkerIndex,
  getLevelTextStyle,
  parseDetailContentLines,
  parseDetailLines,
  parseUpdateNoticeDetailItems,
  replaceHashHeadingMarker,
} from '../data/updateNoticeDetailContent';

import useUpdateNoticeLanguage
  from '../hooks/useUpdateNoticeLanguage';

import './UpdateNoticePage.css';

function normalizeUserAuthList(value) {
  if (Array.isArray(value)) {
    return value
      .map((auth) =>
        String(auth || '')
          .trim()
          .toUpperCase(),
      )
      .filter(Boolean);
  }

  return String(value || '')
    .split('|')
    .map((auth) =>
      String(auth || '')
        .trim()
        .toUpperCase(),
    )
    .filter(Boolean);
}

export default function UpdateNoticeDetailPage() {
  const {
    seq,
  } = useParams();

  const {
    t,
  } = useTranslation('app');

  const {
    user,
  } = useAuth();

  const {
    noticeLanguage,
  } = useUpdateNoticeLanguage();

  const canEditUpdateNotice =
    useMemo(
      () => {
        const authList =
          normalizeUserAuthList(
            user?.userAuth ||
            user?.user_auth,
          );

        return authList.includes(
          'HW',
        );
      },
      [
        user?.userAuth,
        user?.user_auth,
      ],
    );

  const [
    notice,
    setNotice,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    errorKey,
    setErrorKey,
  ] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadNotice = async () => {
      setLoading(true);
      setErrorKey('');
      setNotice(null);

      try {
        const result =
          await getUpdateNotice(
            seq,
            noticeLanguage,
          );

        if (!mounted) {
          return;
        }

        if (!result) {
          setNotice(null);

          setErrorKey(
            'updatenotices.detail.notFound',
          );

          return;
        }

        setNotice(result);
      } catch (loadError) {
        if (!mounted) {
          return;
        }

        console.error(
          '[UpdateNoticeDetailPage] 공지 조회 실패:',
          loadError,
        );

        setNotice(null);

        setErrorKey(
          'updatenotices.detail.loadFailed',
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadNotice();

    return () => {
      mounted = false;
    };
  }, [
    noticeLanguage,
    seq,
  ]);

  const sourceDetails =
    useMemo(
      () =>
        String(
          notice?.details ||
          notice?.overview ||
          '',
        ),
      [
        notice?.details,
        notice?.overview,
      ],
    );

  /*
   * "1. 제목" 또는 "# 제목" 기준으로
   * 상세 본문을 섹션 단위로 분리합니다.
   */
  const detailItems =
    useMemo(
      () =>
        parseUpdateNoticeDetailItems(
          sourceDetails,
        ),
      [sourceDetails],
    );

  const imageSources =
    useMemo(
      () =>
        Array.isArray(
          notice?.images,
        )
          ? notice.images
          : [],
      [notice?.images],
    );

  const linkEntries =
    useMemo(
      () =>
        detailItems.flatMap(
          (item) => {

            if (item.isHashHeading) {
              return (
                extractTopLevelNumberedLines(
                  item.content,
                ).map(
                  (
                    line,
                    index,
                  ) => ({
                    key:
                      `${item.id}-sub-${index}`,

                    targetId:
                      `${item.id}-subline-${index + 1}`,

                    label:
                      line,

                    isNested:
                      true,
                  }),
                )
              );
            }

            return [
              {
                key:
                  item.id,

                targetId:
                  item.id,

                label:
                  `${item.order}. ${item.title}`,

                isNested:
                  false,
              },
            ];
          },
        ),
      [detailItems],
    );

  const displayTitle =
    useMemo(
      () =>
        notice
          ? getNoticeTitle(
              notice,
            ) ||
            notice.title ||
            '-'
          : '-',
      [
        notice,
      ],
    );

  const scrollToDetail = (
    event,
    targetId,
  ) => {
    event.preventDefault();

    const target =
      document.getElementById(
        targetId,
      );

    target?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  /**
   * [image:n] 또는 [image:n|caption]을 렌더링합니다.
   */
  const renderImageMarker = ({
    line,
    key,
    contentLevel,
    forceNormalWeight = false,
  }) => {
    const markerIndex =
      getImageMarkerIndex(
        line,
      );

    if (markerIndex === null) {
      return null;
    }

    const imageSource =
      imageSources[
        markerIndex
      ];

    const caption =
      getImageMarkerCaption(
        line,
      );

    if (!imageSource) {
      return (
        <p
          key={key}
          className="updatenotice-missing-image"
          style={getLevelTextStyle(
            contentLevel,
            forceNormalWeight,
          )}
        >
          {t(
            'updatenotices.detail.missingImage',
            {
              number:
                markerIndex + 1,
            },
          )}
        </p>
      );
    }

    return (
      <figure
        key={key}
        className="updatenotice-image-figure"
      >
        <img
          src={imageSource}
          onLoad={(event) => {
            const image = event.currentTarget;
            image.classList.toggle(
              'is-tall-2x',
              image.naturalHeight >= image.naturalWidth * 2,
            );
            image.classList.toggle(
              'is-tall-3x',
              image.naturalHeight >= image.naturalWidth * 3,
            );
            image.classList.toggle(
              'is-tall-4x',
              image.naturalHeight >= image.naturalWidth * 4,
            );
          }}
          alt={t(
            'updatenotices.detail.imageAlt',
            {
              number:
                markerIndex + 1,
            },
          )}
          className="updatenotice-detail-image"
        />

        {caption && (
          <figcaption className="updatenotice-image-caption">
            {caption}
          </figcaption>
        )}
      </figure>
    );
  };

  const renderPlainContentWithImages = (
    content,
  ) => {
    const lines =
      parseDetailLines(
        content,
      );

    let currentHeadingLevel = 1;

    return (
      <div className="updatenotice-render-blocks">
        {lines.map(
          (
            line,
            index,
          ) => {
            const trimmed =
              String(
                line || '',
              ).trim();

            const isHashHeading =
              /^#{1,6}(?:\s*.*)?$/.test(
                trimmed,
              );

            const numberingLevel =
              getHierarchicalNumberingLevel(
                line,
              );

            if (isHashHeading) {
              currentHeadingLevel = 1;
            }

            if (
              numberingLevel !==
              null
            ) {
              currentHeadingLevel =
                numberingLevel;
            }

            const contentLevel =
              numberingLevel ||
              getChildContentLevel(
                currentHeadingLevel,
              );

            const imageElement =
              renderImageMarker({
                line,

                key:
                  `plain-image-${index}`,

                contentLevel,
              });

            if (imageElement) {
              return imageElement;
            }

            if (!trimmed) {
              return (
                <div
                  key={`plain-blank-${index}`}
                  className="updatenotice-detail-blank"
                />
              );
            }

            return (
              <p
                key={`plain-line-${index}`}
                className="updatenotice-detail-line"
                style={getLevelTextStyle(
                  contentLevel,
                )}
              >
                {replaceHashHeadingMarker(
                  line,
                )}
              </p>
            );
          },
        )}
      </div>
    );
  };

  const renderDetailContent = (
    content,
    anchorPrefix,
    baseLevelOffset = 0,
    forceNormalWeight = false,
  ) => {
    const blocks =
      parseDetailContentLines(
        content,
      );

    if (
      blocks.length === 0
    ) {
      return null;
    }

    let numberedLineIndex = 0;
    let currentHeadingLevel = 1;

    return (
      <div className="updatenotice-render-blocks">
        {blocks.map(
          (
            block,
            index,
          ) => {
            const numberingLevel =
              getHierarchicalNumberingLevel(
                block.text,
              );

            const normalWeight =
              forceNormalWeight &&
              numberingLevel ===
                null;

            if (
              block.type ===
              'subheading'
            ) {
              currentHeadingLevel =
                Math.min(
                  (
                    numberingLevel ||
                    block.level
                  ) +
                    baseLevelOffset,
                  4,
                );
            } else if (
              numberingLevel !==
              null
            ) {
              currentHeadingLevel =
                Math.min(
                  numberingLevel +
                    baseLevelOffset,
                  4,
                );
            }

            const contentLevel =
              block.type ===
              'subheading'
                ? currentHeadingLevel
                : numberingLevel !==
                    null
                  ? Math.min(
                      numberingLevel +
                        baseLevelOffset,
                      4,
                    )
                  : getChildContentLevel(
                      currentHeadingLevel,
                    );

            if (
              block.type ===
              'blank'
            ) {
              return (
                <div
                  key={`blank-${index}`}
                  className="updatenotice-detail-blank"
                />
              );
            }

            if (
              block.type ===
              'subheading'
            ) {
              return (
                <p
                  key={`subheading-${index}`}
                  className="updatenotice-detail-subheading"
                  style={getLevelTextStyle(
                    contentLevel,
                    normalWeight,
                  )}
                >
                  {replaceHashHeadingMarker(
                    block.text,
                  )}
                </p>
              );
            }

            const imageElement =
              renderImageMarker({
                line:
                  block.text,

                key:
                  `detail-image-${index}`,

                contentLevel,

                forceNormalWeight:
                  normalWeight,
              });

            if (imageElement) {
              return imageElement;
            }

            const shouldCreateAnchor =
              Boolean(
                anchorPrefix,
              ) &&
              block.level === 1 &&
              TOP_LEVEL_NUMBERED_LINE_REGEX.test(
                String(
                  block.text ||
                  '',
                ).trim(),
              );

            if (
              shouldCreateAnchor
            ) {
              numberedLineIndex += 1;
            }

            return (
              <p
                key={`detail-line-${index}`}
                id={
                  shouldCreateAnchor
                    ? `${anchorPrefix}-${numberedLineIndex}`
                    : undefined
                }
                className="updatenotice-detail-line"
                style={getLevelTextStyle(
                  contentLevel,
                  normalWeight,
                )}
              >
                {replaceHashHeadingMarker(
                  block.text,
                )}
              </p>
            );
          },
        )}
      </div>
    );
  };

  if (loading) {
    return (

      <div
        className="
      updatenotice-state-card
      updatenotice-loading
    "
        role="status"
        aria-live="polite"
      >
        <span
          className="updatenotice-loading-spinner"
          aria-hidden="true"
        />

        <span className="updatenotice-loading-text">
          {t(
            'updatenotices.detail.loading',
          )}
        </span>
      </div>
    );
  }

  if (
    errorKey ||
    !notice
  ) {
    return (
      <div className="updatenotice-detail-state">
        <div className="updatenotice-state-card error">
          {t(
            errorKey ||
            'updatenotices.detail.notFound',
          )}
        </div>

        <div className="updatenotice-detail-actions">
          <Link
            to="/updatenotices"
            className="updatenotice-secondary-button"
          >
            {t(
              'updatenotices.detail.backToList',
            )}
          </Link>
        </div>
      </div>
    );
  }

  const hasDetailsContent =
    sourceDetails.trim().length >
      0 ||
    detailItems.length > 0;

  const hasTopLevelLinks =
    linkEntries.length > 0;

  return (
    <article className="updatenotice-detail-card">
      <header className="updatenotice-detail-head">
        <div className="updatenotice-detail-tags">
          <span>
            {notice.program ||
              'Hi-Portal'}
          </span>

          {String(notice.program || '').trim().toLowerCase() !== 'hi-link' && (
            <span>
              {notice.platform ||
                t(
                  'updatenotices.detail.commonPlatform',
                )}
            </span>
          )}

          <span>
            {notice.version ||
              '-'}
          </span>
        </div>

        <h2 className="updatenotice-detail-title">
          {displayTitle}
        </h2>

        <div className="updatenotice-detail-meta">
          <span>
            {t(
              'updatenotices.detail.noticeNumber',
              {
                number:
                  notice.seq ||
                  seq,
              },
            )}
          </span>

          <span>
            {t(
              'updatenotices.detail.date',
            )}
            {': '}
            {formatNoticeDate(
              notice.date,
            )}
          </span>

          <div className="updatenotice-detail-actions">
            <Link
              to="/updatenotices"
              className="updatenotice-secondary-button"
            >
              {t(
                'updatenotices.detail.backToList',
              )}
            </Link>

            {/*
             * user.userAuth에 HW가 있는 경우에만
             * 수정 버튼을 표시합니다.
             */}
            {canEditUpdateNotice && (
              <Link
                to={`/updatenotices/admin/${seq}/edit`}
                className="updatenotice-primary-button"
              >
                {t(
                  'updatenotices.detail.edit',
                )}
              </Link>
            )}
          </div>
        </div>
      </header>

      {hasDetailsContent ? (
        <section className="updatenotice-detail-section">
          {hasTopLevelLinks && (
            <>
              <h3 className="updatenotice-whats-new-title">
                ◎{' '}
                {t(
                  'updatenotices.detail.whatsNew',
                )}
              </h3>

              <nav
                className="updatenotice-detail-links"
                aria-label={t(
                  'updatenotices.detail.whatsNew',
                )}
              >
                {linkEntries.map(
                  (entry) => (
                    <a
                      key={entry.key}
                      href={`#${entry.targetId}`}
                      className={
                        entry.isNested
                          ? 'is-nested'
                          : undefined
                      }
                      onClick={(
                        event,
                      ) => {
                        scrollToDetail(
                          event,
                          entry.targetId,
                        );
                      }}
                    >
                      {entry.label}
                    </a>
                  ),
                )}
              </nav>
            </>
          )}

          {detailItems.length >
          0 ? (
            <div className="updatenotice-detail-items">
              {detailItems.map(
                (item) => (
                  <section
                    key={item.id}
                    id={item.id}
                    className="updatenotice-item-block"
                  >
                    <h3 className="updatenotice-item-title">
                      {item.isHashHeading
                        ? `◎${item.title ? ` ${item.title}` : ''}`
                        : `${item.order}. ${item.title}`}
                    </h3>

                    {item.content &&
                      renderDetailContent(
                        item.content,

                        `${item.id}-subline`,

                        0,

                        item.isHashHeading,
                      )}
                  </section>
                ),
              )}
            </div>
          ) : (
            renderPlainContentWithImages(
              sourceDetails,
            )
          )}
        </section>
      ) : (
        <div className="updatenotice-state-card">
          {t(
            'updatenotices.detail.noDetails',
          )}
        </div>
      )}
    </article>
  );
}
